import CircularProgress from '@mui/material/CircularProgress'
import ListSubheader from '@mui/material/ListSubheader'
import React, { useEffect, useState } from 'react'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { Close } from '@mui/icons-material'
import Select from '@mui/material/Select'

import ContainedButton from 'shared/components/Buttons/ContainedButton'

interface Producer {
   uuid: string
   name: string
}

interface ProducerSelectProps {
   producerOptions: Producer[]
   selectedProducers: string[]
   setSelectedProducers: (ids: string[]) => void
   searchPhrase: string
   setSearchPhrase: (value: string) => void
   isFetching: boolean
   t: (key: string) => string
   allSelectedProducers: string[]
}

export const ProducerSelect = React.memo(
   ({
      producerOptions,
      selectedProducers,
      setSelectedProducers,
      searchPhrase,
      setSearchPhrase,
      isFetching,
      t,
      allSelectedProducers,
   }: ProducerSelectProps) => {
      const [visibleCount, setVisibleCount] = useState(100)

      useEffect(() => {
         setVisibleCount(100)
      }, [producerOptions])

      const filteredOptions = producerOptions.filter(
         (p) => !allSelectedProducers.includes(p.uuid) || selectedProducers.includes(p.uuid)
      )

      const visibleProducers = filteredOptions.slice(0, visibleCount)
      const hasMore = filteredOptions.length > visibleCount

      const menuItems = [
         ...visibleProducers.map((producer) => (
            <MenuItem key={producer.uuid} value={producer.uuid}>
               {producer.name}
            </MenuItem>
         )),
         hasMore && (
            <MenuItem
               key="show-more"
               onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  setVisibleCount((prev) => prev + 100)
               }}
               sx={{ textAlign: 'center' }}
            >
               <ContainedButton sx={{ m: '0 auto' }}>{t('showMore')}</ContainedButton>
            </MenuItem>
         ),
      ].filter(Boolean)

      return (
         <Select
            multiple
            onClose={() => setSearchPhrase('')}
            fullWidth
            displayEmpty
            value={selectedProducers}
            onChange={(e) => {
               const values = e.target.value as string[]
               setSelectedProducers(values)
            }}
            renderValue={() => {
               return t('LOCATIONS:favouriteProducers.producers')
            }}
            MenuProps={{
               disableAutoFocusItem: true,
               PaperProps: { style: { maxHeight: 400, width: 300, padding: 0 } },
            }}
         >
            <ListSubheader disableSticky>
               <TextField
                  autoFocus
                  fullWidth
                  placeholder={t('TRANSLATION:search')}
                  value={searchPhrase}
                  onChange={(e) => {
                     e.preventDefault()
                     e.stopPropagation()
                     setSearchPhrase(e.target.value)
                  }}
                  slotProps={{
                     input: {
                        endAdornment: (
                           <>
                              {isFetching && <CircularProgress size={20} />}
                              {searchPhrase && (
                                 <IconButton onClick={() => setSearchPhrase('')}>
                                    <Close />
                                 </IconButton>
                              )}
                           </>
                        ),
                     },
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
               />
            </ListSubheader>

            {!!searchPhrase && searchPhrase.length > 2 && menuItems.length ? (
               menuItems
            ) : (
               <MenuItem disabled>{t('noResults')}</MenuItem>
            )}
         </Select>
      )
   }
)
